import { debugTimerTile } from './DebugTimerTile';
import { TileArtwork, tileArtworkChildren } from '@/utils/tileArtwork';
import { ObservableMap } from 'mobx';
import { isTexturePending } from './tileTextureCache';
import { recolorTileDetails } from '@/utils/tileGraphic';
import { TileType } from '@/models/TileTypes';
import { AndTile } from '@/models/tiles/AndTile';
import { BridgeTile } from '@/models/tiles/BridgeTile';
import { ButtonTile } from '@/models/tiles/ButtonTile';
import { ClockTile } from '@/models/tiles/ClockTile';
import { DelayTile } from '@/models/tiles/DelayTile';
import { DiodeTile } from '@/models/tiles/DiodeTile';
import { SingleInterfaceTile } from '@/models/tiles/SingleInterfaceTile';
import { MultiInterfaceTile } from '@/models/tiles/MultiInterfaceTile';
import { LeverTile } from '@/models/tiles/LeverTile';
import { InverterTile } from '@/models/tiles/NotTile';
import { StructureTile } from '@/models/tiles/StructureTile';
import { RandomTile } from '@/models/tiles/RandomTile';
import {
  TileDefinition,
  TileMeta,
  allConnected,
  allDisconnected,
  type ConnectionFlags,
} from '@/models/tiles/TileDefinition';
import { setupDefaultMeta, TileInstance } from '@/models/tiles/TileInstance';
import { BundleInterfaceTile } from '@/models/tiles/BundleInterfaceTile';
import { WireTile } from '@/models/tiles/WireTile';
import * as PIXI from 'pixi.js';
import { CircuitType } from '@/schemas/common';
import { InterfaceTileState } from '@/schemas/tiles';
import type { IChipEditorModel, TileTemplate } from '@/interfaces/models';
import { InterfaceLinkInstanceId } from '@/types/ids';
import { Side } from '@/schemas/common';
import { InterfaceLink } from '../ChipModel';

const graphHooks = [
  'buildAndConnectNodes',
  'getGraphBuildVisitKey',
  'graphConfiguration',
  'wirelessChannel',
  'saveState',
  'restoreState',
  'syncStateToNodes',
  'needsStateToNodeSync',
  'syncState',
] as const;
function descriptorInChain(value: object, key: string): PropertyDescriptor | undefined {
  for (
    let object: object | null = value;
    object;
    object = Object.getPrototypeOf(object)
  ) {
    const descriptor = Object.getOwnPropertyDescriptor(object, key);
    if (descriptor) return descriptor;
  }
}
const nativeGraphFields = [...graphHooks, 'ioSides'];
const builtinGraphDefinitions = new Map<
  TileType,
  {
    definition: TileDefinition<unknown>;
    hooks: unknown[];
    io: TileDefinition<unknown>['ioSides'];
    sides: unknown[];
    properties: (PropertyDescriptor | undefined)[];
  }
>();

export class TileRegistry {
  private registry = new Map<TileType, TileDefinition<unknown>>();

  register<TState = null>(tileDefinition: TileDefinition<TState>) {
    this.registry.set(tileDefinition.type, tileDefinition as TileDefinition<unknown>);
  }

  types() {
    return this.registry.keys();
  }

  has(type: TileType) {
    return this.registry.has(type);
  }

  /** Unknown/replaced builders retain conservative geometric dependencies. */
  hasNativeGraphBuilders(types: Iterable<TileType>): boolean {
    if (this.get !== nativeRegistryGet) return false;
    for (const type of types) {
      const original = builtinGraphDefinitions.get(type);
      const definition = this.registry.get(type);
      if (
        !original ||
        definition !== original.definition ||
        nativeGraphFields.some((key, index) => {
          const descriptor = descriptorInChain(definition, key);
          const prior = original.properties[index];
          return (
            descriptor?.get !== prior?.get ||
            descriptor?.set !== prior?.set ||
            descriptor?.value !== prior?.value
          );
        }) ||
        graphHooks.some((key, index) => definition[key] !== original.hooks[index]) ||
        definition.ioSides !== original.io ||
        Object.values(Side).some((side, index) => {
          const port = Object.getOwnPropertyDescriptor(definition.ioSides, side);
          if (
            !port ||
            !('value' in port) ||
            !port.value ||
            typeof port.value !== 'object'
          )
            return true;
          const type = Object.getOwnPropertyDescriptor(port.value, 'type');
          const forced = Object.getOwnPropertyDescriptor(port.value, 'forceConnection');
          return (
            !type ||
            !('value' in type) ||
            !forced ||
            !('value' in forced) ||
            type.value !== original.sides[index * 2] ||
            forced.value !== original.sides[index * 2 + 1]
          );
        })
      )
        return false;
    }
    return true;
  }

  get(type: TileType): TileDefinition<unknown> {
    const tileDefinition = this.registry.get(type);

    if (!tileDefinition) {
      throw new Error(`Tile definition not found: ${type}`);
    }

    return tileDefinition;
  }
}

const nativeRegistryGet = TileRegistry.prototype.get;

export const tileDefinitionRegistry = new TileRegistry();

tileDefinitionRegistry.register(new WireTile());
tileDefinitionRegistry.register(debugTimerTile(true));
tileDefinitionRegistry.register(debugTimerTile(false));
tileDefinitionRegistry.register(new BundleInterfaceTile());
tileDefinitionRegistry.register(new DelayTile());
tileDefinitionRegistry.register(new RandomTile());
tileDefinitionRegistry.register(new DiodeTile());
tileDefinitionRegistry.register(new InverterTile());
tileDefinitionRegistry.register(new AndTile());
tileDefinitionRegistry.register(new BridgeTile());
tileDefinitionRegistry.register(new ClockTile());
tileDefinitionRegistry.register(new ButtonTile());
tileDefinitionRegistry.register(new SingleInterfaceTile());
tileDefinitionRegistry.register(new MultiInterfaceTile());
tileDefinitionRegistry.register(new StructureTile());
tileDefinitionRegistry.register(new LeverTile());

for (const type of tileDefinitionRegistry.types()) {
  const definition = tileDefinitionRegistry.get(type);
  builtinGraphDefinitions.set(type, {
    definition,
    properties: nativeGraphFields.map((key) => descriptorInChain(definition, key)),
    hooks: graphHooks.map((key) => definition[key]),
    io: definition.ioSides,
    sides: Object.values(Side).flatMap((side) => [
      definition.ioSides[side]?.type,
      definition.ioSides[side]?.forceConnection,
    ]),
  });
}

export const imageMap = new ObservableMap<TileType, string>();
export const templateImageMap = new ObservableMap<string, string>();

const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isTestEnv =
  typeof (globalThis as any).process !== 'undefined' &&
  (globalThis as any).process.env?.NODE_ENV === 'test';

const texture = (() => {
  if (!isBrowser) {
    return null;
  }

  try {
    return PIXI.RenderTexture.create({
      width: 120,
      height: 120,
      resolution: 2.5,
    });
  } catch (error) {
    if (!isTestEnv) {
      console.warn('Failed to create PIXI render texture for tile registry.', error);
    }
    return null;
  }
})();

let renderer: PIXI.Renderer | null = null;
let rendererReady: Promise<void> | undefined;
export function initializeTileRenderer(): Promise<void> {
  if (!isBrowser || isTestEnv) return Promise.resolve();
  return (rendererReady ??= PIXI.autoDetectRenderer({ preference: 'webgl' }).then(
    (value) => {
      renderer = value;
    },
    (error) => {
      rendererReady = undefined;
      throw error;
    },
  ));
}

/** Render a palette tile with the same graphics as a placed instance.
 * Reuses the registry renderer; never creates a WebGL context per chip.
 */
export function drawInterfacePreview(
  target: CanvasRenderingContext2D,
  tile: TileInstance,
  link: InterfaceLink | undefined,
  size: number,
): (() => void) | undefined {
  if (!renderer || !texture) return undefined;
  let disposed = false;
  const waiting = new Set<PIXI.Texture>();
  const draw = () => {
    if (disposed) return;
    const artwork = new TileArtwork();
    const g = artwork.graphics;
    try {
      tileDefinitionRegistry.get(tile.tileType).graphics.draw(g, {
        meta: tile.meta,
        state: tile.state,
        connections: allDisconnected,
        context: { interfaceLink: link, showLabels: true },
      });
      if (tile.meta.tint) recolorTileDetails(g, parseInt(tile.meta.tint.slice(1), 16));
      artwork.pivot.set(60, 60);
      artwork.position.set(60, 60);
      artwork.rotation = ((tile.meta.rotation ?? 0) * Math.PI) / 2;
      for (const child of tileArtworkChildren(g)) {
        if (!(child instanceof PIXI.Sprite)) continue;
        const base = child.texture;
        if (isTexturePending(base) && !waiting.has(base)) {
          waiting.add(base);
          base.once('update', draw);
        }
      }
      renderer!.render({ container: artwork, target: texture, clear: true });
      target.imageSmoothingEnabled = false;
      target.clearRect(0, 0, size, size);
      target.drawImage(
        renderer!.extract.canvas(texture) as HTMLCanvasElement,
        0,
        0,
        size,
        size,
      );
    } finally {
      artwork.destroy();
    }
  };
  draw();
  return () => {
    disposed = true;
    for (const base of waiting) base.off('update', draw);
    waiting.clear();
  };
}

export const addTileImage = <TState = unknown>(
  tileType: TileType,
  customize?: (meta: TileMeta, state: TState, connections: ConnectionFlags) => void,
) => {
  if (!renderer || !texture) {
    return;
  }

  const artwork = new TileArtwork();
  const g = artwork.graphics;

  const tileDef = tileDefinitionRegistry.get(tileType);
  const meta = setupDefaultMeta();
  const connections: ConnectionFlags = { ...allDisconnected };
  const state = tileDefinitionRegistry.get(tileType).createDefaultState({
    meta,
    globalTilePosition: {
      chipInstanceId: 'test-chip-instance-id',
      location: { x: 0, y: 0 },
      circuitType: CircuitType.ChipLogic,
    },
  }) as TState;
  customize?.(meta, state, connections);

  tileDef.graphics.draw(g, {
    state,
    meta,
    connections,
    context: { isTileImageGeneration: true },
  });

  renderer.render({ container: artwork, target: texture, clear: true });

  // Extract the rendered target directly to retain its 300 physical pixels.
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  imageMap.set(tileType, renderer.extract.canvas(texture).toDataURL!());
  artwork.destroy();
};

export const generateTileImages = async () => {
  await initializeTileRenderer();
  for (const type of tileDefinitionRegistry.types()) {
    addTileImage(type, (_, __, connections) => {
      if (type === TileType.Wire) Object.assign(connections, allConnected);
    });
  }
};

const tintedTemplateImages = new Map<string, string>();

export const getTileTemplateImage = (
  template: TileTemplate,
  tint?: string,
): string | undefined => {
  const baseImage = imageMap.get(template.tileType);

  // Check if we have enough context to render a custom interface tile image
  const interfaceLink = template.context?.interfaceLink;
  const hasStateOverrides = template.state && Object.keys(template.state).length > 0;

  if (
    !isBrowser ||
    (!interfaceLink && !hasStateOverrides && !template.renderOverrides?.meta && !tint) ||
    !renderer ||
    !texture
  ) {
    return baseImage;
  }

  const key = `${interfaceLink ? `${template.id}:${interfaceLink.label}:${interfaceLink.color}` : template.id}:${tint ?? 'default'}`;
  const cache = tint ? tintedTemplateImages : templateImageMap;
  const cached = cache.get(key);
  if (cached) {
    return cached;
  }

  const artwork = new TileArtwork();
  const g = artwork.graphics;
  const tileDef = tileDefinitionRegistry.get(template.tileType);
  const meta = setupDefaultMeta();
  const connections: ConnectionFlags = {
    ...(template.tileType === TileType.Wire ? allConnected : allDisconnected),
  };
  const state = tileDef.createDefaultState({
    meta,
    globalTilePosition: {
      chipInstanceId: 'test-chip-instance-id',
      location: { x: 0, y: 0 },
      circuitType: CircuitType.ChipLogic,
    },
  }) as InterfaceTileState<unknown>;

  // Apply state overrides (e.g. interfaceLinkId)
  if (template.state) {
    // Basic merge for overrides
    Object.assign(state, template.state);
  }

  if (template.renderOverrides?.state) {
    Object.assign(state, template.renderOverrides.state);
  }

  if (template.renderOverrides?.meta) {
    Object.assign(meta, template.renderOverrides.meta);
  }

  // Ensure interfaceLinkId is set if not already from overrides
  if (interfaceLink && !state?.connectedInterface?.interfaceLinkId) {
    if (!state.connectedInterface) {
      state.connectedInterface = {
        interfaceLinkId: interfaceLink.id,
        chipInstanceId: 'test-chip-instance-id',
        circuitType: CircuitType.ChipLogic,
      };
    } else {
      state.connectedInterface.interfaceLinkId = interfaceLink.id;
    }
  }

  let chipEditorModel: IChipEditorModel | undefined;
  if (interfaceLink) {
    const interfaceLinkMap = new Map<InterfaceLinkInstanceId, InterfaceLink>();
    interfaceLinkMap.set(interfaceLink.id, interfaceLink);

    chipEditorModel = {
      chipModel: {
        interfaceLinks: [interfaceLink],
        interfaceLinkMap: interfaceLinkMap,
      },
    } as IChipEditorModel;
  }

  tileDef.graphics.draw(g, {
    state,
    meta,
    connections,
    context: { isTileImageGeneration: true },
    chipEditorModel,
  });

  if (tint) recolorTileDetails(g, Number.parseInt(tint.slice(1), 16));
  renderer.render({ container: artwork, target: texture, clear: true });
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const dataUrl = renderer.extract.canvas(texture).toDataURL!();
  if (tint && cache.size >= 256) cache.delete(cache.keys().next().value!);
  cache.set(key, dataUrl);
  artwork.destroy();

  return dataUrl;
};

const interfaceTiles: TileType[] = [TileType.SingleInterface, TileType.MultiInterface];

export const isInterfaceTileType = (tileType: TileType): boolean => {
  return interfaceTiles.includes(tileType);
};

export const isInterfaceTile = (
  tileInstance: TileInstance,
): tileInstance is TileInstance & { state: InterfaceTileState<unknown> } => {
  return isInterfaceTileType(tileInstance.tileType);
};
